import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { ReagentItem, ReagentItemForm, ReagentItemSearchForm } from 'entities/reagent_item'

type ReagentItemsResponse = PagingResponse & {
    results: ReagentItem[]
}

export function useFetchReagentItemsApi(searchForm?: Form<ReagentItemSearchForm>): IndexApiSet<ReagentItemsResponse> & { execute: () => void } {
    const apiPath = 'reagent_items/'
    const api = useIndexApi<ReagentItemsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvReagentItemsApi(searchForm: Form<ReagentItemSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'reagent_items/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'reagent_item', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostReagentItemApi(): ApiSet<BaseResponse> & { execute: (form: Form<ReagentItemForm>) => void } {
    const apiSet = usePostApi<BaseResponse, ReagentItemForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ReagentItemForm>) => {
        const apiPath = `reagent_items/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutReagentItemApi(): ApiSet<BaseResponse> & { execute: (form: Form<ReagentItemForm>) => void } {
    const apiSet = usePutApi<BaseResponse, ReagentItemForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<ReagentItemForm>) => {
        const apiPath = `reagent_items/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutReagentItemsApi(): ApiSet<BaseResponse> & { execute: (form: Form<ReagentItemForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, ReagentItemForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ReagentItemForm[]>) => {
        const apiPath = `reagent_items/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteReagentItemApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `reagent_items/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
